import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Button } from '@mui/material';
import ChatMessageBubble, { FooterActions } from './ChatMessageBubble';
import CloseIcon from '@mui/icons-material/Close';
import { useUpdateChatMessage } from 'hooks/chats/useUpdateChatMessage';

export type StreamingMessageType = any;

export type StreamingMessageBubbleProps = {
  streamingMessage: StreamingMessageType | undefined | null;
  isBoardChat: boolean;
};

const StreamingMessageBubble: React.FC<StreamingMessageBubbleProps> = ({
  streamingMessage,
  isBoardChat,
}) => {
  const scrollableRef = React.useRef<HTMLDivElement | null>(null);
  const [showBubble, setShowBubble] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const { mutateAsync: updateChatMessage } = useUpdateChatMessage();

  const calculateReadingTime = (text: string): number => {
    if (!text) return 1500;
    const words = text.trim().split(/\s+/).length;

    return Math.min(
      10000,
      Math.max(1500, Math.ceil((words / 300) * 60 * 1000))
    );
  };

  // Clear timeout when component unmounts
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (streamingMessage?.attributes?.isStreaming) {
      setShowBubble(true);
    } else if (
      streamingMessage?.attributes?.error ||
      streamingMessage?.attributes?.isStreaming === false
    ) {
      // Only start the timeout if not hovered
      if (!isHovered) {
        const timeoutDuration = calculateReadingTime(
          streamingMessage?.attributes?.content || ''
        );

        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Set new timeout
        timeoutRef.current = setTimeout(() => {
          setShowBubble(false);
        }, timeoutDuration);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    streamingMessage?.attributes?.isStreaming,
    streamingMessage?.attributes?.error,
    streamingMessage?.attributes?.content,
    isHovered, // Add isHovered as dependency
  ]);

  // Restart timeout when mouse leaves if streaming is complete
  const handleMouseEnter = () => {
    console.log('Mouse entered bubble');
    setIsHovered(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleMouseLeave = () => {
    console.log('Mouse left bubble');
    setIsHovered(false);
    // Only restart timeout if streaming is complete
    if (
      streamingMessage &&
      !streamingMessage.attributes?.isStreaming &&
      showBubble
    ) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const timeoutDuration = calculateReadingTime(
        streamingMessage?.attributes?.content || ''
      );

      timeoutRef.current = setTimeout(() => {
        setShowBubble(false);
      }, timeoutDuration);
    }
  };

  if (!streamingMessage) return null;

  const handleActionClick = (
    type: FooterActions,
    messageID: string,
    secondaryID?: string
  ) => {
    updateChatMessage({
      messageID,
      chatMessageRatingTypeID: Number(secondaryID),
    });
    switch (type) {
      case 'like':
        updateChatMessage({
          messageID,
          chatMessageRatingTypeID: Number(secondaryID),
        });
        break;
      case 'dislike':
        updateChatMessage({
          messageID,
          chatMessageRatingTypeID: Number(secondaryID),
        });
        break;
      case 'duplicate':
        break;
    }
  };

  console.log('streamingMessage', streamingMessage);

  const isLoading = !streamingMessage.attributes.content;

  return (
    <AnimatePresence>
      {isBoardChat && streamingMessage && showBubble && (
        <>
          {/* Interaction-blocking overlay */}
          <Box
            sx={{
              position: isLoading ? '' : 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.01)', // Nearly invisible but enough to capture events
              pointerEvents: 'none',
              cursor: 'default',
              zIndex: 999,
              // Ensure all events are captured by this element
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              },
            }}
            onClick={() => setShowBubble(false)}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            onMouseMove={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
          />

          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            ref={scrollableRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseOver={() => setIsHovered(true)}
            onMouseOut={() => setIsHovered(false)}
            onWheel={(e) => {
              // Prevent wheel events from propagating to elements behind
              e.stopPropagation();
              // Only prevent default if we need to scroll within this element
              const el = scrollableRef.current;
              if (!el) return;

              const { scrollTop, scrollHeight, clientHeight } = el;

              if (scrollTop === 0 && e.deltaY < 0) {
                // At top and scrolling up - let default happen
                return;
              }
              if (
                scrollHeight &&
                clientHeight &&
                scrollTop + clientHeight >= scrollHeight &&
                e.deltaY > 0
              ) {
                // At bottom and scrolling down - let default happen
                return;
              }
              // Otherwise prevent default
              e.preventDefault();
            }}
            sx={{
              position: 'absolute',
              bottom: '100%',
              left: 0,
              right: 0,
              margin: '0 auto 20px auto',
              width: '100%',
              maxHeight: '60vh',
              overflowY: 'auto',
              overflowX: 'hidden',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: '800px',
              backgroundColor: 'white',
              borderRadius: '8px',
              scrollbarWidth: 'none',
              border: (theme) => `1px solid ${theme.palette.borderColor}`,
              pointerEvents: 'auto',
              overflow: 'visible',
            }}
          >
            {/* Close button */}
            <Button
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                backgroundColor: 'var(--color-error-main)',
                color: 'var(--color-white)',
                minWidth: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '1px',
                '&.MuiButton-root:hover': {
                  backgroundColor: 'var(--color-error-main)',
                },
                borderRadius: '7px',
                zIndex: 1050,
              }}
              onClick={() => setShowBubble(false)}
            >
              <CloseIcon fontSize='inherit' />
            </Button>

            <Box
              position='relative'
              width='100%'
              overflow='auto'
              sx={{ borderRadius: '8px', scrollbarWidth: 'none' }}
            >
              <ChatMessageBubble
                id={streamingMessage.id}
                message={streamingMessage.attributes.content}
                isUserMessage={false}
                isStreaming={streamingMessage.attributes.isStreaming || false}
                isLastMessage={true}
                error={streamingMessage.attributes.error || null}
                infoCardStyles={{
                  width: '100%',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  overflow: 'auto',
                }}
                containerStyles={{
                  width: '100%',
                  maxWidth: '100%',
                }}
                onActionClick={handleActionClick}
                PluginsUsed={streamingMessage.attributes.functionCalls || []}
                pluginsPill={streamingMessage.attributes.pluginsPill}
                boardWorkingPill={streamingMessage.attributes.boardWorkingPill}
              />
            </Box>
          </Box>
        </>
      )}
    </AnimatePresence>
  );
};

export default StreamingMessageBubble;
